<template>
  <!-- A fixed item (Overview) while customizing: shown in place, but nothing about it can change. -->
  <div
    v-if="editing && inert"
    class="tw:w-full tw:flex tw:items-center tw:h-11 tw:pl-1 tw:pr-1 tw:rounded-xl tw:text-slate-500 tw:select-none tw:cursor-default"
    :data-tour="link.entry.tourId ? `nav-${link.entry.tourId}` : undefined"
    :title="`${link.label} is always in the sidebar`"
  >
    <span class="tw:w-5 tw:shrink-0" aria-hidden="true"></span>
    <span class="tw:flex tw:items-center tw:flex-1 tw:min-w-0 tw:ml-1 tw:opacity-70">
      <SidebarTile :icon="link.icon" :color="link.color" />
      <span class="tw:ml-3 tw:text-sm tw:truncate tw:min-w-0">{{ link.label }}</span>
    </span>
    <span class="tw:w-7 tw:h-7 tw:flex tw:items-center tw:justify-center tw:shrink-0" aria-hidden="true">
      <Lock class="tw:w-3.5 tw:h-3.5" />
    </span>
  </div>

  <div
    v-else-if="editing"
    class="sidebar-row tw:w-full tw:flex tw:items-center tw:h-11 tw:pl-1 tw:pr-1 tw:rounded-xl tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800/90 tw:select-none tw:group/item"
    data-kind="link"
  >
    <span class="drag-handle tw:w-5 tw:h-8 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:cursor-grab tw:text-slate-500 tw:hover:text-slate-100" title="Drag to move">
      <GripVertical class="tw:w-4 tw:h-4" />
    </span>
    <button
      type="button"
      class="tw:flex-1 tw:min-w-0 tw:h-full tw:flex tw:items-center tw:ml-1 tw:text-left tw:cursor-pointer"
      :title="`Edit ${link.label}`"
      @click="$emit('edit')"
    >
      <SidebarTile :icon="link.icon" :color="link.color" />
      <span class="tw:ml-3 tw:text-sm tw:truncate tw:min-w-0">{{ link.label }}</span>
      <Pencil class="tw:w-3.5 tw:h-3.5 tw:ml-2 tw:shrink-0 tw:opacity-0 tw:group-hover/item:opacity-100 tw:transition-opacity" aria-hidden="true" />
    </button>
    <button
      type="button"
      class="tw:w-7 tw:h-7 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:cursor-pointer tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800"
      :aria-label="`Options for ${link.label}`"
      aria-haspopup="true"
      @click="menu?.toggle($event)"
    >
      <EllipsisVertical class="tw:w-4 tw:h-4" />
    </button>
    <Menu ref="menu" :model="menuItems" popup />
  </div>

  <router-link
    v-else
    :to="{ name: link.routeName, params: { orgId: organizationId } }"
    :data-tour="link.entry.tourId ? `nav-${link.entry.tourId}` : undefined"
    :title="link.label !== link.entry.label ? link.entry.label : undefined"
    class="tw:w-full tw:flex tw:items-center tw:rounded-xl tw:transition-all tw:duration-200 tw:group/item tw:relative tw:overflow-hidden tw:cursor-pointer tw:select-none tw:no-underline"
    :class="[
      variant === 'desktop' ? 'tw:h-11 tw:px-3' : 'tw:h-12 tw:px-4',
      active
        ? 'tw:bg-indigo-600 tw:text-white tw:font-medium tw:shadow-md tw:shadow-indigo-600/10 tw:hover:bg-indigo-500'
        : 'tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800/90 tw:hover:backdrop-blur-md',
      collapsedDesktop ? 'tw:hover:w-[236px] tw:hover:shadow-premium tw:hover:z-50' : ''
    ]"
    @click="$emit('navigate')"
  >
    <div
      v-if="active"
      class="tw:absolute tw:left-0 tw:top-3 tw:bottom-3 tw:w-1 tw:rounded-r tw:bg-[#FF5352]"
    ></div>

    <SidebarTile :icon="link.icon" :color="link.color" />

    <span
      class="tw:ml-3 tw:text-sm tw:truncate nav-label"
      :class="{
        'tw:opacity-0 tw:translate-x-0 tw:pointer-events-none tw:group-hover/item:opacity-100 tw:group-hover/item:translate-x-0 tw:group-hover/item:pointer-events-auto': collapsedDesktop
      }"
    >
      {{ link.label }}
    </span>

    <span
      v-if="link.badge"
      class="tw:absolute tw:right-3 tw:px-1.5 tw:py-0.5 tw:text-xs tw:font-bold tw:rounded-md tw:bg-indigo-500/20 tw:text-indigo-300 tw:border tw:border-indigo-500/30 nav-label"
      :class="{
        'tw:opacity-0 tw:scale-50 tw:pointer-events-none tw:group-hover/item:opacity-100 tw:group-hover/item:scale-100 tw:group-hover/item:pointer-events-auto': collapsedDesktop
      }"
    >
      {{ link.badge }}
    </span>

    <span
      v-if="collapsedDesktop && link.badge"
      class="tw:absolute tw:top-2 tw:right-2 tw:w-2 tw:h-2 tw:rounded-full tw:bg-indigo-500 tw:group-hover/item:opacity-0 tw:transition-opacity"
    ></span>
  </router-link>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import { EllipsisVertical, GripVertical, Lock, Pencil } from '@lucide/vue'
import SidebarTile from './SidebarTile.vue'
import type { ResolvedSidebarLink } from './useSidebarLayout'

const props = defineProps<{
  link: ResolvedSidebarLink
  organizationId: string | number | null
  variant: 'desktop' | 'mobile'
  isPinned: boolean
  active: boolean
  editing: boolean
  /** A fixed item: while customizing it is shown but cannot be clicked, moved or edited. */
  inert?: boolean
}>()

const emit = defineEmits<{
  navigate: []
  edit: []
  remove: []
}>()

const menu = ref<InstanceType<typeof Menu> | null>(null)

// The 72px icon rail: labels hide until the row is hovered, when it flies out to full width.
const collapsedDesktop = computed(() => props.variant === 'desktop' && !props.isPinned)

const menuItems: MenuItem[] = [
  { label: 'Edit', icon: 'pi pi-pencil', command: () => emit('edit') },
  { label: 'Remove', icon: 'pi pi-trash', command: () => emit('remove') }
]
</script>

<style scoped>
.nav-label {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
